import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import apiClient from "../../../services/apiClient";
import Pagination from "../../../components/Pagination";
import { refinePages } from "../../../utils/paginationUtils";
import { CONTAINER_MAX_WIDTH } from "../../../styles/layout";

// 오류 신고 탭. 학원이 화면을 찍어 보내면 우리에게 바로 알림이 간다.
//
// 사진을 여러 장 받는 것이 요점이다. "단어 화면이 이상해요" 만으로는 되짚는 데
// 하루가 걸린다. 학원은 이미 카톡으로 화면을 찍어 보내고 있었다.
const MAX_PHOTOS = 10;

// 한 쪽에 세 건. 글마다 사진과 답변이 붙어서 길다.
const PAGE_SIZE = 3;

const STATUS_COLOR = {
  received: "#E0863C",
  working: "#186a96",
  done: "#00A396",
  hold: "#8a949c",
};

const ErrorReportPage = () => {
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);
  const [rows, setRows] = useState([]);
  const fileRef = useRef(null);
  // 첫 화면 팝업이 `?report=12` 로 보낸다. 그 글을 눈에 띄게 하고 그리로 스크롤한다.
  const [searchParams] = useSearchParams();
  const highlightId = Number(searchParams.get("report")) || 0;
  const highlightRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);

  const isMobile = Math.min(window.innerWidth, window.screen.width) <= 768;

  const load = () => {
    apiClient
      .get("/api/v1/support/reports", { withAuth: true })
      .then((response) => setRows(response.data.rows || []))
      .catch(() => {});
  };

  useEffect(load, []);

  // 이 화면을 열었으면 처리 결과를 본 것이다. 팝업이 다시 뜨지 않게 표시한다.
  useEffect(() => {
    apiClient
      .post("/api/v1/support/reports/seen", {}, { withAuth: true })
      .catch(() => {});
  }, []);

  // 팝업이 가리키는 글이 뒷 쪽에 있으면 그 쪽으로 먼저 넘긴다. 안 그러면
  // 링크를 눌러도 첫 쪽만 보여 어디로 온 것인지 알 수 없다.
  useEffect(() => {
    if (!highlightId) return;
    const order = rows.findIndex((row) => row.id === highlightId);
    if (order >= 0) setCurrentPage(Math.floor(order / PAGE_SIZE) + 1);
  }, [highlightId, rows]);

  useEffect(() => {
    if (!highlightId || !highlightRef.current) return;
    highlightRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightId, rows, currentPage]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const addPhotos = (event) => {
    const picked = Array.from(event.target.files || []);
    const merged = [...photos, ...picked].slice(0, MAX_PHOTOS);
    if (photos.length + picked.length > MAX_PHOTOS) {
      window.alert(`사진은 ${MAX_PHOTOS}장까지 올릴 수 있습니다.`);
    }
    setPhotos(merged);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const submit = async () => {
    if (text.trim().length < 5) {
      window.alert("어떤 문제인지 조금만 더 적어 주세요.");
      return;
    }
    setSending(true);
    try {
      const form = new FormData();
      form.append("text", text.trim());
      photos.forEach((file) => form.append("photos", file));
      await apiClient.post("/api/v1/support/reports", form, {
        withAuth: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setText("");
      setPhotos([]);
      load();
      window.alert("접수했습니다. 24시간 안에 확인해 답변드리겠습니다.");
    } catch (error) {
      const message = error?.response?.data?.error;
      window.alert(message || "접수에 실패했습니다. 잠시 뒤 다시 시도해 주세요.");
    }
    setSending(false);
  };

  return (
    <div
      style={{
        maxWidth: `${CONTAINER_MAX_WIDTH}px`,
        margin: "0 auto",
        padding: isMobile ? "16px 14px 40px" : "24px 28px 60px",
      }}
    >
      <div className="section-title" style={{ marginBottom: "8px" }}>
        오류 신고
      </div>
      <div
        style={{
          background: "#EFF5F5",
          borderLeft: "3px solid #00A396",
          padding: "14px 16px",
          fontSize: "14px",
          lineHeight: 1.7,
          color: "#3A434E",
          marginBottom: "20px",
        }}
      >
        온라인 학습에서 문제가 생기면 <b>사진과 내용을 적어 주세요. 24시간 안에
        해결해 드리겠습니다.</b> 어느 학생의 어느 과제인지, 어떤 화면이었는지 함께
        적어 주시면 훨씬 빨리 찾습니다. 사진은 {MAX_PHOTOS}장까지 올릴 수 있습니다.
      </div>

      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder="예) 3학년 김하연 학생 단어 과제에서 정답을 맞게 썼는데 틀리다고 나옵니다. 8월 28일에 출제한 과제입니다."
        style={{
          width: "100%",
          minHeight: "130px",
          padding: "13px 14px",
          border: "1px solid #D3DBE0",
          fontSize: "15px",
          lineHeight: 1.7,
          fontFamily: "inherit",
          resize: "vertical",
        }}
      />

      <div style={{ margin: "12px 0" }}>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={addPhotos}
          style={{ display: "none" }}
        />
        <button
          type="button"
          onClick={() => fileRef.current && fileRef.current.click()}
          style={{
            border: "1px solid #D3DBE0",
            background: "#fff",
            padding: "9px 16px",
            fontSize: "14px",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          사진 추가 ({photos.length}/{MAX_PHOTOS})
        </button>
      </div>

      {photos.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "14px" }}>
          {photos.map((file, index) => (
            <div key={`${file.name}-${index}`} style={{ position: "relative" }}>
              <img
                src={URL.createObjectURL(file)}
                alt={file.name}
                style={{ width: "82px", height: "82px", objectFit: "cover", border: "1px solid #E3EBEA" }}
              />
              <button
                type="button"
                onClick={() => removePhoto(index)}
                style={{
                  position: "absolute",
                  top: "-6px",
                  right: "-6px",
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  border: "none",
                  background: "#0E141B",
                  color: "#fff",
                  fontSize: "13px",
                  cursor: "pointer",
                  lineHeight: "22px",
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={sending}
        style={{
          background: sending ? "#8a949c" : "#00A396",
          color: "#fff",
          border: "none",
          fontWeight: 800,
          fontSize: "15px",
          padding: "13px 26px",
          cursor: sending ? "default" : "pointer",
        }}
      >
        {sending ? "보내는 중..." : "신고 보내기"}
      </button>

      {rows.length > 0 && (
        <>
          <div className="section-title" style={{ margin: "40px 0 12px" }}>
            내가 보낸 신고
            <span
              style={{
                marginLeft: "8px",
                fontSize: "13px",
                fontWeight: 500,
                color: "#8a949c",
              }}
            >
              {rows.length}건
            </span>
          </div>
          <div style={{ display: "grid", gap: "10px" }}>
            {pageRows.map((row) => (
              <div
                key={row.id}
                ref={row.id === highlightId ? highlightRef : null}
                style={{
                  border:
                    row.id === highlightId
                      ? "2px solid #00A396"
                      : "1px solid #E3EBEA",
                  background: "#fff",
                  padding: "14px 16px",
                }}
              >
                <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "6px" }}>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 800,
                      color: "#fff",
                      background: STATUS_COLOR[row.status] || "#8a949c",
                      padding: "3px 9px",
                    }}
                  >
                    {row.status === "done" ? "처리완료 ✅" : row.status_text}
                  </span>
                  <span style={{ fontSize: "13px", color: "#8a949c" }}>
                    {String(row.created_at).slice(0, 10)}
                  </span>
                </div>
                <div style={{ fontSize: "14px", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {row.text}
                </div>
                {row.photo_url_list.length > 0 && (
                  <div style={{ display: "flex", gap: "6px", marginTop: "8px", flexWrap: "wrap" }}>
                    {row.photo_url_list.map((url) => (
                      <a key={url} href={url} target="_blank" rel="noreferrer">
                        <img
                          src={url}
                          alt="첨부"
                          style={{ width: "62px", height: "62px", objectFit: "cover", border: "1px solid #E3EBEA" }}
                        />
                      </a>
                    ))}
                  </div>
                )}
                {/* 답변은 학원에게 보여 준다. 무엇이 어떻게 됐는지 남는 자리가
                    여기 하나다(2026-09-01 결정, 그 전에는 상태만 보여 줬다). */}
                {row.reply && (
                  <div
                    style={{
                      marginTop: "12px",
                      background: "#F2F8F7",
                      borderLeft: "3px solid #00A396",
                      padding: "11px 13px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        fontWeight: 800,
                        color: "#00A396",
                        marginBottom: "5px",
                      }}
                    >
                      답변
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        lineHeight: 1.7,
                        whiteSpace: "pre-wrap",
                        color: "#3A434E",
                      }}
                    >
                      {row.reply}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination
            visiblePages={refinePages({
              total_pages: totalPages,
              current_page: currentPage,
            })}
            current_page={currentPage}
            handlePageClick={setCurrentPage}
          />
        </>
      )}
    </div>
  );
};

export default ErrorReportPage;
